import { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { BillingService } from '../billing.service'

/**
 * The payment channels a payer may actually choose.
 *
 * Every screen used to carry its own hardcoded list of five VA banks. That list
 * could not follow the routing an operator configures, so a channel switched off
 * stayed on offer and one switched on never appeared — and it had no way to show
 * QRIS or an e-wallet at all.
 *
 * The value handed back is the WIRE code, because that is what the checkout
 * endpoints expect. The catalog code stays internal. The full method object
 * comes with it as a second argument, so a caller rendering an order summary can
 * use the server-priced `app_fee_idr` instead of keeping its own fee table.
 */
export default function PaymentMethodPicker({ companyId, amountIdr, value, onChange, disabled, autoSelect = true }) {
  const [methods, setMethods] = useState(null) // null = loading
  const [error, setError] = useState(null)

  // Debounced: amountIdr changes on every keystroke in the top-up field, and in
  // cloud mode each call is a proxy hop to SAW against a shared rate limit.
  useEffect(() => {
    if (!companyId) return undefined
    let alive = true
    setError(null)
    // A refetch means the question changed (amount, company) — the list on
    // screen no longer answers it, so drop to loading rather than let a stale
    // empty result flash "no methods" in the gap.
    setMethods((m) => (m && m.length ? m : null))
    const pricedFor = amountIdr || null
    const timer = setTimeout(() => {
      BillingService.getPaymentMethods(companyId, amountIdr ? { amount_idr: amountIdr } : undefined)
      .then((res) => {
        if (!alive) return
        const list = res?.data?.data ?? res?.data ?? []
        // Stamp what this price answers, so a caller freezing the fee into an
        // order summary can tell a fresh quote from a stale one.
        setMethods(Array.isArray(list) ? list.map((m) => ({ ...m, priced_for_idr: pricedFor })) : [])
      })
      .catch((err) => {
        if (!alive) return
        setMethods([])
        setError(
          err?.response?.data?.message ||
            err?.data?.message ||
            err?.message ||
            'Failed to load payment methods.',
        )
      })
    }, 300)
    return () => {
      alive = false
      clearTimeout(timer)
    }
  }, [companyId, amountIdr])

  // Held in a ref so the effect below can depend on the data alone. Callers pass
  // an inline arrow, whose identity changes every render — with onChange in the
  // dependency array, re-emitting the selection would re-trigger the effect and
  // spin forever.
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Re-emit on every load, not only when the selection is invalid. The fee rides
  // along with the method object, and the first fetch usually happens before the
  // amount is known — so a "selection is still valid, nothing to do" shortcut
  // left the caller holding a fee of zero while the invoice charged the real one.
  //
  // A load that comes back empty WITHDRAWS the selection with onChange(null,
  // null): the parent is still holding a wire code this list no longer offers,
  // and its submit button gates on that value being set.
  useEffect(() => {
    if (methods === null) return
    if (!methods.length) {
      if (value) onChangeRef.current(null, null)
      return
    }
    const keep = value && methods.find((m) => m.wire_code === value)
    // autoSelect=false: never pick a method for the caller. A screen that offers
    // another funding source alongside the gateway (the enquiry Confirm-payment
    // modal has "Everest saldo") must not have the picker grab methods[0] on
    // mount — that overrides saldo, and since saldo hands us value=null the effect
    // would re-grab it on every render, making saldo impossible to keep selected.
    // Only keep the caller's OWN gateway choice fresh; emit solely on their click.
    if (!autoSelect) {
      if (keep) onChangeRef.current(keep.wire_code, keep)
      return
    }
    const pick = keep || methods[0]
    onChangeRef.current(pick.wire_code, pick)
  }, [methods, value, autoSelect])

  if (!companyId) {
    // No company, no price list. The eternal spinner here would hide a wiring
    // mistake as "still loading".
    return <p className="text-sm-regular text-error/600">Company not recognized.</p>
  }
  if (methods === null) {
    return <p className="text-sm-regular text-gray/500">Loading payment methods…</p>
  }
  if (error) {
    return <p className="text-sm-regular text-error/600">{error}</p>
  }
  if (!methods.length) {
    // Not an empty state to shrug at: nobody can pay until an operator routes a
    // method, so say what is wrong rather than showing an empty box.
    return (
      <p className="text-sm-regular text-error/600">
        No payment method is active yet. Contact the SAW admin to
        enable one.
      </p>
    )
  }

  const groups = methods.reduce((acc, m) => {
    ;(acc[m.category_label] ||= []).push(m)
    return acc
  }, {})

  return (
    <div className="flex flex-col gap-4">
      {Object.entries(groups).map(([label, items]) => (
        <div key={label}>
          <p className="mb-2 text-xs-medium uppercase tracking-wide text-gray/500">{label}</p>
          <div className="flex flex-col gap-2">
            {items.map((m) => {
              const selected = value === m.wire_code
              return (
                <button
                  key={m.code}
                  type="button"
                  disabled={disabled}
                  onClick={() => onChange(m.wire_code, m)}
                  className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
                    selected ? 'border-brand/600 bg-brand/50' : 'border-gray/200 hover:bg-gray/50'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                >
                  <MethodIcon code={m.code} src={m.icon_url} fallback={m.code.replace(/^VA_/, '').slice(0, 3)} />
                  <span className="flex-1 text-sm-medium text-gray/900">{m.name}</span>
                  {m.app_fee_idr != null && m.app_fee_idr > 0 && (
                    <span className="text-xs-regular text-gray/500">
                      +{Number(m.app_fee_idr).toLocaleString('id-ID')}
                    </span>
                  )}
                  {/* Radio affordance — the border tint alone was too quiet to
                      answer "which one did I pick?" at a glance. */}
                  <span
                    className={`flex size-4 shrink-0 items-center justify-center rounded-full border ${
                      selected ? 'border-brand/600 bg-brand/600' : 'border-gray/300'
                    }`}
                  >
                    {selected ? <span className="size-1.5 rounded-full bg-white" /> : null}
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

PaymentMethodPicker.propTypes = {
  companyId: PropTypes.string.isRequired,
  amountIdr: PropTypes.number,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  autoSelect: PropTypes.bool,
}
PaymentMethodPicker.defaultProps = { amountIdr: null, value: null, disabled: false }

// Brand background per method — so the badge reads as an intentional bank icon,
// not a broken image. Keyed by catalog code.
const BRAND_BG = {
  VA_BCA: '#0066B3',
  VA_BNI: '#F05A22',
  VA_BRI: '#00529C',
  VA_MANDIRI: '#003D79',
  VA_PERMATA: '#00A88E',
  VA_CIMB: '#7A1F2B',
  QRIS: '#E5231B',
  GOPAY: '#00AAE4',
  OVO: '#4C2A86',
  DANA: '#118EEA',
  LINKAJA: '#E62129',
  SHOPEEPAY: '#EE4D2D',
  CREDIT_CARD: '#475467',
}

// Payment-method icon. The seeded icon_url points at external favicon proxies
// (Google/DuckDuckGo) that fail on-prem/offline or under CSP, leaving a grey
// "broken" badge (Everest #86ey2j64p). So the default is now a self-contained
// brand-coloured badge that always renders; a real logo is used only when the
// icon_url is an inline data: URI (self-hosted), never an external fetch.
function MethodIcon({ code, src, fallback }) {
  const isInline = typeof src === 'string' && src.startsWith('data:')
  if (isInline) {
    // Box is a 56×32 rectangle, not a tight square: most Indonesian bank marks
    // are wide wordmarks (BCA, BRI, Mandiri, CIMB Niaga), which a 24px square
    // squeezes into an illegible sliver. object-contain letterboxes symbol marks
    // (OVO, LinkAja) in the same footprint so the icon column stays aligned.
    return (
      <span className="inline-flex h-8 w-14 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray/200 bg-white px-1">
        <img src={src} alt="" loading="lazy" className="max-h-6 max-w-full object-contain" />
      </span>
    )
  }
  return (
    <span
      className="inline-flex h-8 w-14 shrink-0 items-center justify-center rounded-md text-[10px] font-bold uppercase leading-none text-white"
      style={{ backgroundColor: BRAND_BG[code] || '#475467' }}
    >
      {fallback}
    </span>
  )
}
MethodIcon.propTypes = { code: PropTypes.string, src: PropTypes.string, fallback: PropTypes.string }
MethodIcon.defaultProps = { code: '', src: null, fallback: '' }
